/**
 * The enrollment funnel, in order:
 *   program_detail_view → enrollment_started → subscription_plan_selected →
 *   payment_initiated → payment_successful → program_enrolled
 * The plan and payment steps exist only on the GSA path, where a program can
 * be started on a paid plan; every step names the program.
 */

import {
  EcommerceParams,
  PlanParams,
  ProgramParams,
  pushDataLayerEvent,
} from './dataLayer';

export interface AnalyticsProgram {
  id: string;
  name: string;
  abbreviation?: string | null;
}

/** A plan at the interval the learner picked. */
export interface AnalyticsPlan {
  /** `free` for staying on the free tier. */
  id: string;
  name: string;
  interval?: string | null;
  /** In major units (12.5, not 1250). */
  value: number;
  currency?: string | null;
}

const programParams = (program: AnalyticsProgram): ProgramParams => ({
  program_id: program.id,
  program_name: program.name,
  program_abbreviation: program.abbreviation,
});

/** Program fields are left out when no program was being started. */
const optionalProgramParams = (program?: AnalyticsProgram | null) =>
  program ? programParams(program) : {};

const planParams = (plan: AnalyticsPlan): PlanParams => ({
  plan_id: plan.id,
  plan_name: plan.name,
  billing_interval: plan.interval,
  value: plan.value,
  currency: plan.currency?.toUpperCase(),
});

const ecommerceParams = (
  transactionId: string,
  plan: AnalyticsPlan
): EcommerceParams => ({
  ecommerce: {
    transaction_id: transactionId,
    value: plan.value,
    currency: plan.currency?.toUpperCase(),
    items: [
      {
        item_id: plan.id,
        item_name: plan.name,
        item_variant: plan.interval,
        price: plan.value,
        quantity: 1,
      },
    ],
  },
});

export const trackProgramDetailView = (program: AnalyticsProgram) =>
  pushDataLayerEvent('program_detail_view', programParams(program));

/** The learner asked to start a program. */
export const trackEnrollmentStarted = (program: AnalyticsProgram) =>
  pushDataLayerEvent('enrollment_started', programParams(program));

/**
 * `program` is the program the learner came to start, absent when they
 * reached the plans from the banner or Settings.
 */
export const trackSubscriptionPlanSelected = (
  plan: AnalyticsPlan,
  program?: AnalyticsProgram | null
) =>
  pushDataLayerEvent('subscription_plan_selected', {
    ...planParams(plan),
    ...optionalProgramParams(program),
  });

/**
 * `payment_initiated` when Stripe's checkout opens, `payment_successful` when
 * it reports the payment done. Both carry GA4's ecommerce object keyed by the
 * checkout session, so the FIT container can send them as `begin_checkout` /
 * `purchase` (GA4 drops a repeat purchase with the same `transaction_id`).
 */
export const trackPayment = (
  event: 'payment_initiated' | 'payment_successful',
  checkoutSessionId: string,
  plan: AnalyticsPlan,
  program?: AnalyticsProgram | null
) =>
  pushDataLayerEvent(event, {
    ...planParams(plan),
    ...optionalProgramParams(program),
    ...ecommerceParams(checkoutSessionId, plan),
  });

interface EnrollingUser {
  sandId?: string | null;
  programEnrollments?: Array<{
    id: string;
    program?: { id: string } | null;
  }> | null;
}

/**
 * Fire `program_enrolled`, plus `first_program_enrolled` when it is the
 * learner's only enrollment. `user` is the `applyToProgram.user` result.
 */
export const trackProgramEnrolled = (
  program: AnalyticsProgram,
  user?: EnrollingUser | null
) => {
  const payload = {
    ...programParams(program),
    program_enrollment_id: user?.programEnrollments?.find(
      (pe) => pe.program?.id === program.id
    )?.id,
    // Left out rather than sent empty, which would wipe the `user_id` that
    // `setDataLayerUser` keeps in the data layer.
    ...(user?.sandId ? { user_id: user.sandId } : {}),
  };
  pushDataLayerEvent('program_enrolled', payload);
  if (user?.programEnrollments?.length === 1) {
    pushDataLayerEvent('first_program_enrolled', payload);
  }
};
